import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Star, ThumbsUp, ThumbsDown, MessageSquare, Filter,
  Loader2, Inbox, Eye, ArrowRight,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { APP_CONFIG } from '../config/appConfig';
import { authFetch } from '../config/authConfig';
import { CURRENT_USER } from '../data/dummyData';

const API = APP_CONFIG.BACKEND_URL;

interface RatingItem {
  id: number;
  conversation_id: string;
  message_id: number | null;
  tipo: 'session' | 'response';
  score: number;
  comentario: string | null;
  created_at: string;
  titulo: string;
  pregunta_usuario?: string;
  respuesta_agente?: string;
}

type FilterType = 'all' | 'session' | 'response';
type FilterScore = 'all' | 'positive' | 'negative' | '1' | '2' | '3' | '4' | '5';

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return `Hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Hace ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Ayer';
  if (days < 7) return `Hace ${days} días`;
  return new Date(iso).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
}

export default function RatingsPage() {
  const { user } = useAuth();
  const userId = user?.id || CURRENT_USER.id;
  const navigate = useNavigate();

  const [items, setItems] = useState<RatingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [filterScore, setFilterScore] = useState<FilterScore>('all');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('user_id', userId);
      if (filterType !== 'all') params.set('tipo', filterType);
      if (filterScore === 'positive') params.set('score', '1');
      else if (filterScore === 'negative') params.set('score', '0');
      else if (['1','2','3','4','5'].includes(filterScore)) params.set('score', filterScore);

      const res = await authFetch(`${API}/api/v1/ratings/user/detail?${params}`);
      if (res.ok) setItems(await res.json());
    } catch (err) {
      console.error('Error loading ratings:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, filterType, filterScore]);

  useEffect(() => { loadData(); }, [loadData]);

  const sessionCount = items.filter(i => i.tipo === 'session').length;
  const responseCount = items.filter(i => i.tipo === 'response').length;
  const positiveCount = items.filter(i => i.tipo === 'response' && i.score === 1).length;
  const negativeCount = items.filter(i => i.tipo === 'response' && i.score === 0).length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Mis Calificaciones</h1>
        <p className="text-sm text-gray-500 mt-1">Revisa las calificaciones que has dado a las sesiones y respuestas del agente.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-center" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <p className="text-xl font-bold text-gray-900">{items.length}</p>
          <p className="text-[11px] text-gray-500">Total calificaciones</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-center" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center justify-center gap-1">
            <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
            <p className="text-xl font-bold text-gray-900">{sessionCount}</p>
          </div>
          <p className="text-[11px] text-gray-500">Sesiones calificadas</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-center" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center justify-center gap-1">
            <ThumbsUp className="w-4 h-4 text-emerald-500" />
            <p className="text-xl font-bold text-emerald-700">{positiveCount}</p>
          </div>
          <p className="text-[11px] text-gray-500">Respuestas positivas</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-center" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center justify-center gap-1">
            <ThumbsDown className="w-4 h-4 text-red-400" />
            <p className="text-xl font-bold text-red-700">{negativeCount}</p>
          </div>
          <p className="text-[11px] text-gray-500">Respuestas negativas</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5">
          <Filter className="w-4 h-4 text-gray-400" />
          <span className="text-xs text-gray-500 font-medium">Tipo:</span>
        </div>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          {([['all','Todas'],['session','Sesiones'],['response','Respuestas']] as const).map(([val, label]) => (
            <button key={val} onClick={() => setFilterType(val)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${filterType === val ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1.5 ml-2">
          <span className="text-xs text-gray-500 font-medium">Calificación:</span>
        </div>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          <button onClick={() => setFilterScore('all')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${filterScore === 'all' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>
            Todas
          </button>
          {filterType !== 'session' && (
            <>
              <button onClick={() => setFilterScore('positive')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${filterScore === 'positive' ? 'bg-white shadow-sm text-emerald-600' : 'text-gray-500'}`}>
                <ThumbsUp className="w-3 h-3" /> Positivas
              </button>
              <button onClick={() => setFilterScore('negative')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${filterScore === 'negative' ? 'bg-white shadow-sm text-red-500' : 'text-gray-500'}`}>
                <ThumbsDown className="w-3 h-3" /> Negativas
              </button>
            </>
          )}
          {filterType !== 'response' && (
            [5,4,3,2,1].map(s => (
              <button key={s} onClick={() => setFilterScore(String(s) as FilterScore)}
                className={`px-2 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-0.5 ${filterScore === String(s) ? 'bg-white shadow-sm text-yellow-600' : 'text-gray-500'}`}>
                {s} <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
              </button>
            ))
          )}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-16">
          <Loader2 className="w-8 h-8 text-gray-300 mx-auto mb-3 animate-spin" />
          <p className="text-sm text-gray-500">Cargando calificaciones...</p>
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16">
          <Inbox className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No se encontraron calificaciones con los filtros seleccionados.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item, i) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-all"
              style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  {item.tipo === 'session' ? (
                    <div className="flex items-center gap-1 px-2 py-1 bg-yellow-50 rounded-lg">
                      <MessageSquare className="w-3.5 h-3.5 text-yellow-600" />
                      <span className="text-[11px] font-medium text-yellow-700">Sesión</span>
                      <div className="flex gap-0.5 ml-1">
                        {[1,2,3,4,5].map(s => (
                          <Star key={s} className={`w-3 h-3 ${s <= item.score ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`} />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg ${item.score === 1 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                      {item.score === 1
                        ? <><ThumbsUp className="w-3.5 h-3.5 text-emerald-500" /><span className="text-[11px] font-medium text-emerald-700">Positiva</span></>
                        : <><ThumbsDown className="w-3.5 h-3.5 text-red-400" /><span className="text-[11px] font-medium text-red-600">Negativa</span></>}
                    </div>
                  )}
                  <span className="text-[10px] text-gray-400">{timeAgo(item.created_at)}</span>
                </div>
                <button
                  onClick={() => navigate('/chat', { state: { conversationId: item.conversation_id } })}
                  className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-700 transition-colors"
                >
                  <Eye className="w-3.5 h-3.5" /> Ver sesión <ArrowRight className="w-3 h-3" />
                </button>
              </div>

              <p className="text-xs font-medium text-gray-800 mb-2">{item.titulo}</p>

              {item.pregunta_usuario && (
                <div className="mb-2 pl-3 border-l-2 border-blue-200">
                  <p className="text-[10px] text-blue-500 font-medium mb-0.5">Pregunta:</p>
                  <p className="text-xs text-gray-600">{item.pregunta_usuario}</p>
                </div>
              )}

              {item.respuesta_agente && (
                <div className="mb-2 pl-3 border-l-2 border-gray-200">
                  <p className="text-[10px] text-gray-400 font-medium mb-0.5">Respuesta del agente:</p>
                  <p className="text-xs text-gray-500 line-clamp-3">{item.respuesta_agente}</p>
                </div>
              )}

              {item.comentario && (
                <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 italic">"{item.comentario}"</p>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
